import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import DictAdmin from './DictAdmin';
import PlanTypeManage from './PlanTypeManage';
import MModeAdmin from './MModeAdmin';
import CategoryAdmin from '../material/CategoryAdmin';
import CraftTypeManage from '../material/CraftTypeManage';
import HelpTip from '../../components/HelpTip';
import { useCan } from '../../store';

// 2026-08-31 统一「数据字典」页: 字典维护 / 加工模式字典 / 材料类别 / 工艺类型 四张原本各自
// 一个菜单项的基础数据维护页收进同一页, 用顶层 tab 切换 (菜单只留一个入口).
// 旧路由 /admin/dict、/admin/mmode、/materials/categories、/craft-types 重定向进来 (App.tsx),
// 深链带 ?d=<tabKey>; 子页自己的 query (如字典维护的 ?tab=process_arch) 与 d 共存.
//
// 权限: 字典维护/加工模式字典原为 admin-only, 材料类别/工艺类型普通用户可看可提工单.
// 所以本页整体不加 PermissionRoute — 需权限的两个 tab 对无权者不显示, 直接敲深链
// (?d=dict) 时由 tab 内的 <PermissionRoute> 兜底拦截.

type TabKey = 'dict' | 'mmode' | 'category' | 'crafttype' | 'plantype';

const TABS: {
    k: TabKey;
    label: string;
    hint: string;
}[] = [
    {
        k: 'dict',
        label: '字典维护',
        hint: '通用字典类目 (加工类型/光源/动作…) 与工艺架构',
    },
    {
        k: 'mmode',
        label: '加工模式字典',
        hint: '加工模式分组/模式条目与设备引用',
    },
    { k: 'category', label: '材料类别', hint: 'material_category 两级类别树' },
    {
        k: 'crafttype',
        label: '工艺类型',
        hint: 'cap_type 工艺类型 (材料工艺能力的取值来源)',
    },
    {
        k: 'plantype',
        label: '方案类型',
        hint: '方案类型 (参数 planType 字段的可选值, 按工艺架构分别维护, 可绑定设备)',
    },
];

function DataDictAdmin() {
    const location = useLocation();
    const history = useHistory();
    // 只列自己能维护的 tab (2026-09-13): 字典维护按类目分组归属 (材料属性/设备能力/参数枚举),
    // 加工模式字典与工艺类型归设备管理员, 材料类别归材料管理员; 词表读接口本身仍是 public.
    const canMaterial = useCan('material', 'update');
    const canDevice = useCan('device', 'update');
    const canParam = useCan('param_row', 'update');
    const canCategory = useCan('category', 'update');
    const canCapType = useCan('cap_type', 'update');
    const canMMode = useCan('mm_mode', 'update');
    const allowTab = (k: TabKey) => {
        if (k === 'dict') return canMaterial || canDevice || canParam;
        if (k === 'mmode') return canMMode;
        if (k === 'category') return canCategory;
        if (k === 'plantype') return canParam; // 归「参数」分组, 与字典维护同口径
        return canCapType; // crafttype
    };
    const visibleTabs = TABS.filter((t) => allowTab(t.k));
    // ?d=<tabKey> 直达; 认不出的值退回第一个 tab.
    const q = new URLSearchParams(location.search);
    const asked = q.get('d') as TabKey | null;
    // 深链指向没权限的 tab 时回落到第一个可见 tab (没有任何可见 tab 时由 RouteGuard 拦在门外)
    const active: TabKey = visibleTabs.some((t) => t.k === asked)
        ? (asked as TabKey)
        : (visibleTabs[0]?.k ?? 'dict');

    // 切 tab 只改 d, 保留子页自己的 query 参数没意义 (换了页面), 所以整串重置
    const switchTab = (k: TabKey) => {
        if (k === active) return;
        history.replace(`${location.pathname}?d=${k}`);
    };

    if (visibleTabs.length === 0) {
        return (
            <div className="py-10 text-center text-sm text-slate-500">
                没有你可维护的基础数据
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap gap-2 items-center">
                <div className="inline-flex rounded-lg bg-slate-100 p-0.5">
                    {visibleTabs.map((t) => (
                        <button
                            key={t.k}
                            type="button"
                            title={t.hint}
                            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                                active === t.k
                                    ? 'bg-white text-brand-700 shadow-sm'
                                    : 'text-slate-500 hover:text-slate-700'
                            }`}
                            onClick={() => switchTab(t.k)}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>
                <HelpTip text="这里维护的都是给业务数据提供取值来源的基础字典: 字典类目/工艺架构、加工模式、材料类别树、工艺类型、方案类型。改动照旧走审批工单。" />
            </div>

            {active === 'dict' && <DictAdmin />}
            {active === 'mmode' && <MModeAdmin />}
            {active === 'category' && <CategoryAdmin />}
            {active === 'crafttype' && <CraftTypeManage />}
            {active === 'plantype' && <PlanTypeManage />}
        </div>
    );
}

export default DataDictAdmin;
